import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Clock3, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const subjects = [
              { name: "French", slug: "French", flag: "🇫🇷", color: "from-blue-500/15 to-blue-500/0" },
  { name: "English", slug: "English", flag: "🇬🇧", color: "from-emerald-500/15 to-emerald-500/0" },
  { name: "Spanish", slug: "Spanish", flag: "🇪🇸", color: "from-amber-500/15 to-amber-500/0" },
  { name: "Math", slug: "Math", flag: "➗", color: "from-purple-500/15 to-purple-500/0" },
];

export default function Home() {
  return (
    <div className="bg-grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/5 text-primary font-medium">
              <Sparkles className="h-3 w-3 mr-1.5" />
              Curated tutors · 1-on-1 live sessions
            </Badge>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05]">
              Master <span className="text-blue-600 dark:text-blue-400">French</span>,{" "}
              <span className="text-emerald-600 dark:text-emerald-400">English</span>,{" "}
              <span className="text-amber-600 dark:text-amber-400">Spanish</span> &{" "}
              <span className="text-purple-600 dark:text-purple-400">Math</span> with expert
              tutors.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Hand-picked native speakers and certified educators ready for personalised, live
              1-on-1 sessions. Browse profiles, compare rates, and send a session request in
              under a minute.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-full h-12 px-7" data-testid="hero-find-tutor">
                <Link to="/tutors">
                  Find a tutor <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-7"
                data-testid="hero-become-tutor"
              >
                <Link to="/become-tutor">Become a tutor</Link>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500 fill-current" /> 4.9 avg rating
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Verified credentials
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-4 w-4 text-primary" /> Replies in under 4h
              </span>
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {subjects.map((s) => (
              <Link
                key={s.name}
                to={`/tutors?subject=${encodeURIComponent(s.slug)}`}
                className={`group rounded-3xl border border-border/70 bg-card p-6 hover:border-primary/40 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br ${s.color}`}
                data-testid={`hero-subject-${s.slug.toLowerCase()}`}
              >
                <div className="text-3xl">{s.flag}</div>
                <div className="mt-4 font-heading text-xl font-semibold">{s.name}</div>
                <div className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1 group-hover:text-primary transition-colors">
                  Browse tutors <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Simple process</p>
              <h2 className="mt-2 text-3xl lg:text-4xl font-heading font-bold tracking-tight">
                From browsing to your first session in three steps
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Browse profiles", d: "Filter by subject, level, hourly rate and availability. Read bios and reviews." },
              { n: "02", t: "Send a request", d: "Pick a date & time and tell your tutor your goals. We'll notify them by email." },
              { n: "03", t: "Learn live", d: "Jump into a live 1-on-1 session. Keep what you want, cancel what you don't." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
                <div className="font-mono text-sm text-primary">{s.n}</div>
                <div className="mt-3 font-heading text-xl font-semibold">{s.t}</div>
                <p className="mt-2 text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight">
            Ready to meet your next tutor?
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Browse our full roster of French, English, Spanish and Math tutors — no sign-up required.
          </p>
          <Button asChild size="lg" className="mt-6 rounded-full h-12 px-7">
            <Link to="/tutors">Explore all tutors</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
